import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { getClientStats } from "../services/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  User,
  Building2,
  Mail,
  Phone,
  MapPin,
  FileText,
  FileMinus,
  FilePlus,
  TrendingUp,
  Edit
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

const STATUS_BADGES = {
  draft: { label: "Brouillon", className: "bg-slate-100 text-slate-600" },
  validated: { label: "Validée", className: "bg-blue-50 text-blue-700" },
  sent: { label: "Envoyée", className: "bg-blue-50 text-blue-700" },
  pending: { label: "En attente", className: "bg-blue-50 text-blue-700" },
  paid: { label: "Payée", className: "bg-green-50 text-green-700" },
  partial: { label: "Partielle", className: "bg-amber-50 text-amber-700" },
  overdue: { label: "En retard", className: "bg-red-50 text-red-700" },
  rectified: { label: "Rectifiée", className: "bg-purple-50 text-purple-700" },
  accepted: { label: "Accepté", className: "bg-green-50 text-green-700" },
  refused: { label: "Refusé", className: "bg-red-50 text-red-700" },
  expired: { label: "Expiré", className: "bg-slate-100 text-slate-600" }
};

const DOC_TYPE_BADGES = {
  invoice: { label: "Facture", className: "bg-teal-50 text-teal-700" },
  quote: { label: "Devis", className: "bg-blue-50 text-blue-700" },
  credit: { label: "Avoir", className: "bg-red-50 text-red-700" }
};

const DOC_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "invoice", label: "Factures" },
  { value: "quote", label: "Devis" },
  { value: "credit", label: "Avoirs" }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR"
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("fr-FR");
};

const ClientDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [docFilter, setDocFilter] = useState("all");

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await getClientStats(customerId);
        setData(res.data);
      } catch (err) {
        toast.error("Client non trouvé");
        navigate("/clients");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [customerId, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#26788A]"></div>
      </div>
    );
  }

  if (!data) return null;

  const chartData = data.monthly?.map(m => ({
    month: m.month_label,
    CA: m.invoiced_ttc,
    Encaissé: m.paid_ttc
  })) || [];

  const documents = (data.documents || []).filter(d => docFilter === "all" || d.doc_type === docFilter);
  const docCounts = (data.documents || []).reduce((acc, d) => {
    acc[d.doc_type] = (acc[d.doc_type] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600 hover:text-slate-900"
            data-testid="client-back-btn"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              {data.customer.customer_type === "individual" ? (
                <User size={18} className="text-[#26788A]" />
              ) : (
                <Building2 size={18} className="text-[#26788A]" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="client-name">{data.customer.name}</h1>
              <p className="text-sm text-slate-500">Fiche client</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="btn-outline"
            onClick={() => navigate(`/clients?edit=${customerId}`)}
            data-testid="client-edit-btn"
          >
            <Edit size={14} className="mr-1.5" />
            Modifier
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="btn-primary" data-testid="client-create-doc-btn">
                <FilePlus size={14} className="mr-1.5" />
                Créer un document
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="client-create-invoice" onClick={() => navigate(`/document/new/invoice?customer=${customerId}`)}>
                <FileText size={14} className="mr-2 text-[#26788A]" />
                Nouvelle facture
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="client-create-quote" onClick={() => navigate(`/document/new/quote?customer=${customerId}`)}>
                <FileText size={14} className="mr-2 text-blue-600" />
                Nouveau devis
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="client-create-credit" onClick={() => navigate(`/document/new/credit?customer=${customerId}`)}>
                <FileMinus size={14} className="mr-2 text-red-500" />
                Nouvel avoir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Customer info + All-time KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-white border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <User size={16} /> Informations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.customer.email && (
              <div className="flex items-center gap-2 text-sm">
                <Mail size={14} className="text-slate-400" />
                <span className="text-slate-700">{data.customer.email}</span>
              </div>
            )}
            {data.customer.phone && (
              <div className="flex items-center gap-2 text-sm">
                <Phone size={14} className="text-slate-400" />
                <span className="text-slate-700">{data.customer.phone}</span>
              </div>
            )}
            {(data.customer.address || data.customer.city) && (
              <div className="flex items-center gap-2 text-sm">
                <MapPin size={14} className="text-slate-400" />
                <span className="text-slate-700">
                  {[data.customer.address, `${data.customer.postal_code || ""} ${data.customer.city || ""}`.trim()].filter(Boolean).join(", ")}
                </span>
              </div>
            )}
            {data.customer.siret && (
              <p className="text-xs text-slate-500 font-mono">SIRET : {data.customer.siret}</p>
            )}
            {data.customer.notes && (
              <p className="text-xs text-slate-500 pt-2 border-t border-slate-100 whitespace-pre-wrap">{data.customer.notes}</p>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2 bg-gradient-to-r from-[#26788A] to-[#1D5E6C] text-white border-0">
          <CardContent className="p-6">
            <p className="text-sm text-white/70 mb-4">CA généré (depuis le début, avoirs déduits)</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-white/70 text-xs">CA total TTC</p>
                <p className="text-2xl font-bold font-mono" data-testid="client-ca-total">{formatCurrency(data.all_time.invoiced_ttc)}</p>
              </div>
              <div>
                <p className="text-white/70 text-xs">Encaissé</p>
                <p className="text-2xl font-bold font-mono text-green-300">{formatCurrency(data.all_time.paid_ttc)}</p>
              </div>
              <div>
                <p className="text-white/70 text-xs">En attente</p>
                <p className="text-2xl font-bold font-mono text-amber-300">{formatCurrency(data.all_time.pending_ttc)}</p>
              </div>
              <div>
                <p className="text-white/70 text-xs">Panier moyen</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(data.all_time.avg_invoice_ttc)}</p>
              </div>
            </div>
            <p className="text-sm text-white/70 mt-4">
              {data.all_time.invoice_count} facture{data.all_time.invoice_count > 1 ? "s" : ""}
              {data.all_time.credited_ttc > 0 && (
                <span className="text-red-200"> · Avoirs émis : {formatCurrency(data.all_time.credited_ttc)}</span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly chart */}
      <Card className="bg-white border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <TrendingUp size={16} /> Évolution (12 derniers mois)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} />
                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(0)}k` : val} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                />
                <Bar dataKey="CA" fill="#26788A" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Encaissé" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Documents history */}
      <Card className="bg-white border-slate-200" data-testid="client-documents-section">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <FileText size={16} /> Historique des documents ({documents.length})
            </CardTitle>
            <div className="flex gap-1.5">
              {DOC_FILTERS.map(f => (
                <button
                  key={f.value}
                  data-testid={`doc-filter-${f.value}`}
                  onClick={() => setDocFilter(f.value)}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                    docFilter === f.value
                      ? "bg-[#26788A] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f.label}
                  {f.value !== "all" && docCounts[f.value] ? ` (${docCounts[f.value]})` : ""}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {documents.length > 0 ? (
            <div className="space-y-1">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/document/${doc.id}`)}
                  data-testid={`client-doc-row-${doc.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-xs px-2 py-1 rounded shrink-0 ${DOC_TYPE_BADGES[doc.doc_type]?.className}`}>
                      {DOC_TYPE_BADGES[doc.doc_type]?.label}
                    </span>
                    <span className="font-mono text-sm font-medium text-slate-900 shrink-0">{doc.number || "Brouillon"}</span>
                    <span className="text-sm text-slate-500 shrink-0">{formatDate(doc.issue_date)}</span>
                    <span className={`text-xs px-2 py-1 rounded shrink-0 ${STATUS_BADGES[doc.status]?.className || "bg-slate-100 text-slate-600"}`}>
                      {STATUS_BADGES[doc.status]?.label || doc.status}
                    </span>
                    {doc.subject && (
                      <span className="text-xs text-slate-400 truncate hidden md:block">{doc.subject}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className={`font-mono text-sm ${doc.doc_type === "credit" ? "text-red-600" : ""}`}>
                      {doc.doc_type === "credit" ? "-" : ""}{formatCurrency(doc.total_ttc)}
                    </span>
                    {doc.doc_type === "invoice" && doc.amount_due > 0 && (
                      <span className="text-xs text-amber-600">Dû: {formatCurrency(doc.amount_due)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">Aucun document</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientDetailPage;
